<template>
  <div class="related-categories" v-if="relatedCategories.length">
    <div class="mb-6 border-b border-gray-300 pb-[1.07rem]">
      <div class="flex items-center space-x-2">
        <Heading
          :level="4"
          color="text-ash-200"
        >
          Related
        </Heading>
      </div>
    </div>

    <CategoriesList
      v-for="cat in relatedCategories"
      :key="cat.title"
      :category="cat"
      class="mb-[1.429rem]"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import Heading from "../../../atom/heading.vue"
import CategoriesList from "../../../atom/categoriesList.vue"
import { URL_CONFIG } from "~~/shared/utils/url.const"
import type {
  HealthConcernItem,
  BundleItem,
  TestItem,
  NormalizedCategory,
  NormalizedItem,
} from "~~/shared/types/common";

const props = defineProps<{
  benefits?: string[];
  healthConcerns?: HealthConcernItem[];
  relatedProductBundles?: BundleItem[];
}>();

const createBenefitUrl = (benefit: string): string =>
  `${URL_CONFIG.PRODUCT_BASE_URL}?f[0]=` +
  encodeURIComponent(URL_CONFIG.BENEFITS + benefit).replace(/%20/g, '+');

const relatedCategories = computed<NormalizedCategory[]>(() => {
  const categories: NormalizedCategory[] = [];

  if (props.benefits?.length) {
    categories.push({
      title: "Benefits",
      items: props.benefits.map<NormalizedItem>((b) => ({
        key: b,
        to: createBenefitUrl(b),
        title: b,
        aria: b,
        id: 'benefits'
      })),
    });
  }

  if (props.healthConcerns?.length) {
    categories.push({
      title: "Health Concerns",
      items: props.healthConcerns.map<NormalizedItem>((hc) => ({
        key: hc.title,
        to: hc.url,
        title: hc.title,
        aria: hc.title,
        id: 'health-concerns'
      })),
    });
  }

  if (props.relatedProductBundles?.length) {
    categories.push({
      title: "Stacks",
      items: props.relatedProductBundles.map<NormalizedItem>((bundle) => ({
        key: bundle.id,
        to: bundle.shopUrl,
        title: bundle.name,
        aria: bundle.name,
        id: 'stacks'
      })),
    });
  }

  return categories;
});
</script>
